import { PartImageCandidate, PartImageEvidenceTier, PartImageItem } from '@/api/generatedApi';
import { ImageMatchConfidencePill } from '@/components/PartFinder/confidence/ConfidencePill';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MouseEvent, ReactNode, useState } from 'react';

export const NO_PICK = '';

export const ItemCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  padding: '12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  transition: 'border-color 120ms, background-color 120ms',
  '& .item-actions .MuiIconButton-root': { color: theme.palette.text.secondary },
  '&:hover, &:focus-within': {
    borderColor: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover .item-actions .MuiIconButton-root, &:focus-within .item-actions .MuiIconButton-root': {
    color: theme.palette.text.primary,
  },
}));

export const ItemActions = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '2px',
  flexShrink: 0,
  marginTop: '-4px',
  marginRight: '-4px',
});

interface ItemHeaderProperties {
  title: ReactNode;
  subtitle: ReactNode;
  description?: string;
  actions: ReactNode;
}

export const ItemHeader = ({ title, subtitle, description, actions }: ItemHeaderProperties) => (
  <Stack direction='row' gap='8px' alignItems='flex-start'>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction='row' gap='8px' alignItems='baseline'>
        <Typography variant='subtitle2'>{title}</Typography>
        <Typography variant='caption' color='text.secondary'>
          {subtitle}
        </Typography>
      </Stack>
      {description && (
        <Typography variant='body2' color='text.secondary'>
          {description}
        </Typography>
      )}
    </Box>
    <ItemActions className='item-actions'>{actions}</ItemActions>
  </Stack>
);

interface ItemIconActionProperties {
  label: string;
  icon: ReactNode;
  testId: string;
  disabled: boolean;
  onClick: (event: MouseEvent<HTMLElement>) => void;
}

export const ItemIconAction = ({ label, icon, testId, disabled, onClick }: ItemIconActionProperties) => (
  <Tooltip title={label}>
    <span>
      <IconButton size='small' aria-label={label} disabled={disabled} onClick={onClick} data-testid={testId}>
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

export const photoList = (photoNumbers: number[]): string =>
  `${photoNumbers.length === 1 ? 'photo' : 'photos'} ${photoNumbers.join(', ')}`;

export const canMeasure = (item: PartImageItem): boolean =>
  item.candidates.every(candidate => candidate.evidenceTier === PartImageEvidenceTier.Inferred);

export const topPick = (item: PartImageItem): string => item.suggestedNiin ?? item.candidates[0]?.niin ?? NO_PICK;

const CandidateOption = ({ candidate }: { candidate: PartImageCandidate }) => (
  <Stack direction='row' gap='8px' alignItems='center' sx={{ minWidth: 0, width: '100%' }}>
    <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }}>
      {candidate.nsn} {candidate.itemName ?? ''}
    </Typography>
    <ImageMatchConfidencePill score={candidate.score} />
  </Stack>
);

interface PartPickerProperties {
  candidates: PartImageCandidate[];
  pick: string;
  disabled: boolean;
  onPick: (niin: string) => void;
}

export const PartPicker = ({ candidates, pick, disabled, onPick }: PartPickerProperties) => (
  <TextField
    select
    size='small'
    fullWidth
    label='Part'
    value={candidates.some(candidate => candidate.niin === pick) ? pick : NO_PICK}
    disabled={disabled}
    onChange={event => onPick(event.target.value)}
    slotProps={{ htmlInput: { 'data-testid': 'part-image-item-pick' } }}
  >
    <MenuItem value={NO_PICK} disabled>
      Pick a part
    </MenuItem>
    {candidates.map(candidate => (
      <MenuItem key={candidate.niin} value={candidate.niin}>
        <CandidateOption candidate={candidate} />
      </MenuItem>
    ))}
  </TextField>
);

interface IncludeCheckboxProperties {
  label: string;
  included: boolean;
  picked: boolean;
  busy: boolean;
  onToggle: () => void;
}

export const IncludeCheckbox = ({ label, included, picked, busy, onToggle }: IncludeCheckboxProperties) => (
  <Tooltip title={picked ? '' : 'Pick a part to include this'} placement='top'>
    <Box component='span' sx={{ alignSelf: 'flex-start' }}>
      <Checkbox
        checked={included}
        onChange={onToggle}
        disabled={busy || !picked}
        slotProps={{ input: { 'aria-label': label } }}
        sx={{ p: 0.5 }}
      />
    </Box>
  </Tooltip>
);

interface ListedItem {
  photoNumbers: number[];
  description: string;
}

interface CollapsibleItemListProperties {
  severity: AlertColor;
  message: ReactNode;
  items: ListedItem[];
  testId: string;
  actionDisabled?: boolean;
  onMeasure?: (position: number) => void;
  onEditCrop?: (position: number) => void;
}

export const CollapsibleItemList = ({
  severity,
  message,
  items,
  testId,
  actionDisabled = false,
  onMeasure,
  onEditCrop,
}: CollapsibleItemListProperties) => {
  const [open, setOpen] = useState(false);
  return (
    <Alert
      severity={severity}
      data-testid={testId}
      action={
        <Button color='inherit' size='small' onClick={() => setOpen(!open)} data-testid={`${testId}-toggle`}>
          {open ? 'Hide' : 'Show'}
        </Button>
      }
    >
      {message}
      <Collapse in={open}>
        <Box component='ul' sx={{ m: 0, mt: 1, pl: 2 }}>
          {items.map((item, index) => (
            <li key={`${item.photoNumbers.join('-')}-${index}`}>
              {item.description || 'A part'} ({photoList(item.photoNumbers)}){' '}
              {onMeasure && (
                <Button
                  size='small'
                  color='inherit'
                  disabled={actionDisabled}
                  onClick={() => onMeasure(index)}
                  data-testid={`${testId}-measure`}
                >
                  Measure…
                </Button>
              )}
              {onEditCrop && (
                <Button
                  size='small'
                  color='inherit'
                  disabled={actionDisabled}
                  onClick={() => onEditCrop(index)}
                  data-testid={`${testId}-edit-crop`}
                >
                  Edit crop
                </Button>
              )}
            </li>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
};
